import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { IPolicy } from "shared";
import { toast } from "react-toastify";

import { Modal } from "../../../../common/Atoms/Modal";
import { Button } from "../../../../common/Atoms/Button";
import { Select } from "../../../../common/Atoms/Select";
import { Notification } from "../../../../common/Atoms/Notification";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useGroups } from "../../../hooks/useGroups";
import { useCopyPolicyMutation } from "../../../services/api/policyApi/policy";

interface ICopyPolicyModalProps {
  open: boolean;
  onClose: () => void;
  policy: IPolicy;
}

export function CopyPolicyModal({
  open,
  onClose,
  policy,
}: ICopyPolicyModalProps) {
  const { data: groups = [] } = useGroups();
  const { organisationId, groupId } = useParams();
  const [copyPolicy, { isLoading }] = useCopyPolicyMutation();
  const [targetGroupId, setTargetGroupId] = useState<number>(Number(groupId));
  const [copied, setCopied] = useState<{ policy: IPolicy; groupId: number } | null>(null);

  useEffect(() => {
    if (open) {
      setTargetGroupId(Number(groupId));
      setCopied(null);
    }
  }, [open]);

  const options = useMemo(() => [...groups]
    .sort((a, b) => b.default ? 1 : -1)
    .map(g => ({
      label: g.title,
      value: g.id,
    })), [groups]);

  async function onCopy() {
    try {
      const res = await copyPolicy({
        organisationId,
        id: policy.id,
        groupId: targetGroupId,
      }).unwrap();

      setCopied({ policy: res.policy, groupId: targetGroupId });
    }
    catch (err) {
      console.error(err);
      toast.error(`Could not copy policy. Please try again.`);
    }
  }

  return (
    <Modal
      title={ `Copy policy` }
      open={ open }
      setOpen={ onClose }
    >
      { copied ? (
        <div className={ `mt-4 space-y-6` }>
          <Notification type={ `success` }>
            { `Copied to ` }
            <Link
              to={ `/${organisationId}/groups/${copied.groupId}/policies/${copied.policy.id}` }
              className={ `underline font-medium` }
              onClick={ onClose }
            >
              { copied.policy.title }
            </Link>
            { `. It is inactive until you turn it on.` }
          </Notification>
          <div className={ `flex justify-end` }>
            <Button
              color={ `gray` }
              onClick={ onClose }
            >
              { `Close` }
            </Button>
          </div>
        </div>
      ) : (
        <div className={ `mt-4 space-y-6` }>
          <Paragraph>
            { `This creates a new policy with the same settings as "${policy.title}". The copy starts as inactive, so it will not do anything until you turn it on.` }
          </Paragraph>

          { options.length > 1 && (
            <Select
              label={ `Copy into group` }
              options={ options }
              selected={ targetGroupId }
              onChange={ ({ value }) => setTargetGroupId(Number(value)) }
            />
          ) }

          <div className={ `flex justify-end space-x-2` }>
            <Button
              color={ `gray` }
              onClick={ onClose }
            >
              { `Cancel` }
            </Button>
            <Button
              onClick={ onCopy }
              loading={ isLoading }
              disabled={ isLoading || !targetGroupId }
            >
              { `Copy Policy` }
            </Button>
          </div>
        </div>
      ) }
    </Modal>
  );
}
